// src/components/WebsiteManager.tsx

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Alert, Button, ButtonGroup, Card, Col, Form, Modal, Row, Spinner, ToggleButton } from 'react-bootstrap';
import {
  Subscription, initialSubscriptionFormData,
  Customer, CustomerFormData, initialCustomerFormData,
  Website, WebsiteAcceptanceSummary, BitbucketRepo,
  initialWebsiteFormData
} from '../types';

import WebsiteTable from '../components/WebsiteTable';
import CreateWebsiteModal from '../components/CreateWebsiteModal';
import CustomerSelectModal from '../components/CustomerSelectModal';
import CreateCustomerModal from '../components/CreateCustomerModal';
import SubscriptionSelectModal from '../components/SubscriptionSelectModal';
import CreateSubscriptionModal from '../components/CreateSubscriptionModal';
import BitbucketSelectModal from '../components/BitbucketSelectModal';
import CreateBitbucketModal from '../components/CreateBitbucketModal';
import './WebsiteManager.css';

type AcceptanceReviewFields = {
  clientEntityType: string;
  signerTitle: string;
  releaseScope: string;
  workReferences: string;
  issuesReviewedBy: string;
  issuesReviewDate: string;
  knownIssuesReviewStatus: string;
  exclusions: string;
};

type AcceptanceFormData = AcceptanceReviewFields & {
  deliveryModel: 'gemstone' | 'third_party';
  releaseVersion: string;
  releaseIdentifier: string;
  releaseDate: string;
  reviewUrl: string;
  productionUrl: string;
  clientLegalName: string;
  clientCompanyNumber: string;
  clientRegisteredAddress: string;
  clientNoticeEmail: string;
  masterAgreementReference: string;
  thirdPartyDeveloper: string;
  acceptanceEvidence: string;
  goLiveAuthorisation: string;
  approvedGoLiveWindow: string;
  supportAllowance: string;
  additionalSupportRate: string;
  signerName: string;
  signerEmail: string;
  notes: string;
  knownIssues: string;
};

type AcceptanceDefaultKey = Exclude<keyof NonNullable<Website['acceptanceDefaults']>, 'savedAt'>;

type AcceptanceGenerationPayload = Record<AcceptanceDefaultKey | keyof AcceptanceReviewFields | 'signerName' | 'signerEmail', string | null>;

const dateInputValue = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const urlFromDomain = (domain: string | null | undefined) => {
  if (!domain) {
    return '';
  }

  return /^https?:\/\//i.test(domain) ? domain : `https://${domain}`;
};

const acceptanceNeedsAttention = (website: Website) => {
  if (!website.testDomain && !website.prodDomain) {
    return false;
  }

  const status = website.latestAcceptance?.status?.toLowerCase();

  return !status || !['accepted', 'completed'].includes(status);
};

const apiErrorMessage = (error: any, fallback: string) => {
  const errors = error?.response?.data?.errors;
  const firstError = errors && typeof errors === 'object'
    ? Object.values(errors).flat().find(Boolean)
    : null;

  return String(firstError || error?.response?.data?.message || error?.response?.data?.error || error?.message || fallback);
};

const apiAuthHeaders = () => ({
  Authorization: `Basic ${btoa(
    `${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`
  )}`,
});

const normaliseOptionalText = (value: string) => {
  const trimmed = value.replace(/\u00a0/g, ' ').trim();

  return trimmed || null;
};

const normaliseEmailInput = (value: string) => {
  let trimmed = value.replace(/\u00a0/g, ' ').trim();
  const displayNameMatch = trimmed.match(/<([^<>]+)>/);

  if (displayNameMatch) {
    trimmed = displayNameMatch[1];
  }

  trimmed = trimmed.replace(/^mailto:/i, '').trim().replace(/[;,]+$/g, '').trim();

  return trimmed || null;
};

const acceptanceDefaultValue = (
  defaults: Website['acceptanceDefaults'],
  key: AcceptanceDefaultKey,
  fallback: string
) => {
  return defaults ? (defaults[key] ?? '') : fallback;
};

const savedDefaultsFromPayload = (payload: AcceptanceGenerationPayload): NonNullable<Website['acceptanceDefaults']> => ({
  ...payload,
  savedAt: new Date().toISOString(),
});

export const generationPayloadFromForm = (form: AcceptanceFormData): AcceptanceGenerationPayload => ({
  clientEntityType: normaliseOptionalText(form.clientEntityType),
  signerTitle: normaliseOptionalText(form.signerTitle),
  releaseScope: normaliseOptionalText(form.releaseScope),
  workReferences: normaliseOptionalText(form.workReferences),
  issuesReviewedBy: normaliseOptionalText(form.issuesReviewedBy),
  issuesReviewDate: normaliseOptionalText(form.issuesReviewDate),
  knownIssuesReviewStatus: normaliseOptionalText(form.knownIssuesReviewStatus),
  exclusions: normaliseOptionalText(form.exclusions),
  signerName: normaliseOptionalText(form.signerName),
  signerEmail: normaliseEmailInput(form.signerEmail),
  deliveryModel: form.deliveryModel,
  releaseVersion: normaliseOptionalText(form.releaseVersion),
  releaseIdentifier: normaliseOptionalText(form.releaseIdentifier),
  releaseDate: normaliseOptionalText(form.releaseDate),
  reviewUrl: normaliseOptionalText(form.reviewUrl),
  productionUrl: normaliseOptionalText(form.productionUrl),
  clientLegalName: normaliseOptionalText(form.clientLegalName),
  clientCompanyNumber: normaliseOptionalText(form.clientCompanyNumber),
  clientRegisteredAddress: normaliseOptionalText(form.clientRegisteredAddress),
  clientNoticeEmail: normaliseEmailInput(form.clientNoticeEmail),
  masterAgreementReference: normaliseOptionalText(form.masterAgreementReference),
  thirdPartyDeveloper: normaliseOptionalText(form.thirdPartyDeveloper),
  acceptanceEvidence: normaliseOptionalText(form.acceptanceEvidence),
  notes: normaliseOptionalText(form.notes),
  knownIssues: normaliseOptionalText(form.knownIssues),
  goLiveAuthorisation: normaliseOptionalText(form.goLiveAuthorisation),
  approvedGoLiveWindow: normaliseOptionalText(form.approvedGoLiveWindow),
  supportAllowance: normaliseOptionalText(form.supportAllowance),
  additionalSupportRate: normaliseOptionalText(form.additionalSupportRate),
});

const generationPayloadFingerprint = (payload: AcceptanceGenerationPayload) => JSON.stringify(payload);

const acceptanceDocuments = (acceptance: WebsiteAcceptanceSummary | null) => {
  return acceptance?.contractGeneration?.documents ?? acceptance?.documents ?? [];
};

const pdfDocumentForAcceptance = (acceptance: WebsiteAcceptanceSummary | null) => {
  return acceptanceDocuments(acceptance).find(document => document.type.toLowerCase() === 'pdf') ?? null;
};

const hasSentAcceptance = (acceptance: WebsiteAcceptanceSummary | null) => {
  const status = (acceptance?.docusignEnvelope?.status ?? acceptance?.status ?? '').toLowerCase();

  return ['sent', 'delivered', 'completed', 'accepted'].includes(status);
};

const acceptanceHistoryLabel = (acceptance: WebsiteAcceptanceSummary) => {
  const hasPdf = Boolean(pdfDocumentForAcceptance(acceptance));
  const status = acceptance.status || 'generated';

  return `${acceptance.releaseVersion} · ${status} · #${acceptance.id} · ${hasPdf ? 'PDF' : 'DOCX only'}`;
};

const WebsiteManager: React.FC = () => {
  const [websites, setWebsites] = useState<Website[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNeedsAcceptanceOnly, setShowNeedsAcceptanceOnly] = useState(false);

  // State for creating new website
  const [showModal, setShowModal] = useState(false);
  const [newWebsiteData, setNewWebsiteData] = useState(initialWebsiteFormData);

  // State for customers
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [currentWebsiteId, setCurrentWebsiteId] = useState<number | null>(null);
  const [showCreateCustomerModal, setShowCreateCustomerModal] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState<CustomerFormData>(initialCustomerFormData);

  // State for subscriptions
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [currentCustomerId, setCurrentCustomerId] = useState<number | null>(null);
  const [showCreateSubscriptionModal, setShowCreateSubscriptionModal] = useState(false);
  const [newSubscriptionData, setNewSubscriptionData] = useState(initialSubscriptionFormData);

  // Bitbucket
  const [bitbucketRepos, setBitbucketRepos] = useState<BitbucketRepo[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showBitbucketModal, setShowBitbucketModal] = useState(false);
  const [showCreateBitbucketModal, setShowCreateBitbucketModal] = useState(false);
  const [newBitbucketRepo, setNewBitbucketRepo] = useState({ name: '' });

  // Docker bitbucket
  const [showDockerBitbucketModal, setShowDockerBitbucketModal] = useState(false);
  const [dockerWebsiteId, setDockerWebsiteId] = useState<number | null>(null);
  const [isDockerMode, setIsDockerMode] = useState(false);

  // -- NEW LOADING STATES --
  // 1) For fetching repos
  const [bitbucketReposLoading, setBitbucketReposLoading] = useState(false);
  // 2) For creating a new repo
  const [creatingRepoLoading, setCreatingRepoLoading] = useState(false);

  // Go-live acceptance workflow
  const [showAcceptanceModal, setShowAcceptanceModal] = useState(false);
  const [acceptanceWebsite, setAcceptanceWebsite] = useState<Website | null>(null);
  const [acceptanceForm, setAcceptanceForm] = useState<AcceptanceFormData>({
    clientEntityType: '',
    signerTitle: '',
    releaseScope: '',
    workReferences: '',
    issuesReviewedBy: '',
    issuesReviewDate: '',
    knownIssuesReviewStatus: '',
    exclusions: '',
    deliveryModel: 'gemstone',
    releaseVersion: '',
    releaseIdentifier: '',
    releaseDate: dateInputValue(),
    reviewUrl: '',
    productionUrl: '',
    clientLegalName: '',
    clientCompanyNumber: '',
    clientRegisteredAddress: '',
    clientNoticeEmail: '',
    masterAgreementReference: '',
    thirdPartyDeveloper: '',
    acceptanceEvidence: '',
    goLiveAuthorisation: '',
    approvedGoLiveWindow: '',
    supportAllowance: '',
    additionalSupportRate: '',
    signerName: '',
    signerEmail: '',
    notes: '',
    knownIssues: '',
  });
  const [generatedAcceptance, setGeneratedAcceptance] = useState<WebsiteAcceptanceSummary | null>(null);
  const [generatedAcceptanceFingerprint, setGeneratedAcceptanceFingerprint] = useState<string | null>(null);
  const [acceptanceHistory, setAcceptanceHistory] = useState<WebsiteAcceptanceSummary[]>([]);
  const [generatingAcceptance, setGeneratingAcceptance] = useState(false);
  const [sendingAcceptance, setSendingAcceptance] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  const [pdfPreviewError, setPdfPreviewError] = useState<string | null>(null);

  // Fetch websites
  const fetchWebsites = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/websites`, {
        headers: {
          'Authorization': `Basic ${btoa(
            `${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`
          )}`
        }
      });
      setWebsites(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load websites');
    }
    setLoading(false);
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/plesk/customers`, {
        headers: {
          'Authorization': `Basic ${btoa(
            `${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`
          )}`
        }
      });
      setCustomers(Array.isArray(response.data.data) ? response.data.data : []);
    } catch (err) {
      console.error('Failed to fetch customers', err);
      setError('Failed to fetch customers');
      setCustomers([]);
    }
  };

  // Subscriptions
  const fetchSubscriptions = async (customerId: number) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/plesk/customers/${customerId}/plesk-subscriptions`,
        {
          headers: {
            'Authorization': `Basic ${btoa(
              `${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`
            )}`
          }
        }
      );
      setSubscriptions(response.data.data);
    } catch (err) {
      console.error('Failed to fetch subscriptions', err);
      setError('Failed to fetch subscriptions');
    }
  };

  // Bitbucket
  const fetchBitbucketRepos = async () => {
    try {
      setBitbucketReposLoading(true); // <--- set loading
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/bitbucket-repos`, {
        headers: {
          Authorization: `Basic ${btoa(
            `${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`
          )}`
        }
      });
      setBitbucketRepos(response.data);
    } catch (err) {
      console.error('Failed to fetch Bitbucket repos', err);
      setError('Failed to fetch Bitbucket repos');
    } finally {
      setBitbucketReposLoading(false); // <--- turn off loading
    }
  };

  // Lifecycle
  useEffect(() => {
    fetchWebsites();
    fetchCustomers();
  }, []);

  useEffect(() => {
    const pdfDocument = pdfDocumentForAcceptance(generatedAcceptance);

    setPdfPreviewUrl(prev => {
      if (prev) {
        URL.revokeObjectURL(prev);
      }

      return null;
    });
    setPdfPreviewError(null);

    if (!showAcceptanceModal || !pdfDocument) {
      setPdfPreviewLoading(false);
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;

    setPdfPreviewLoading(true);

    axios.get(
      `${process.env.REACT_APP_API_URL}/contract-documents/${pdfDocument.id}/content`,
      {
        headers: apiAuthHeaders(),
        responseType: 'blob',
      }
    )
      .then((response) => {
        if (cancelled) {
          return;
        }

        objectUrl = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        setPdfPreviewUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) {
          setPdfPreviewError('PDF preview could not be loaded.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setPdfPreviewLoading(false);
        }
      });

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [generatedAcceptance, showAcceptanceModal]);

  // ============== Handlers ==============
  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);

  const handleBuildDocker = async (websiteId: number) => {
    try {
      // 1) Find the Website object
      const website = websites.find((w) => w.id === websiteId);
      if (!website) {
        alert("Website not found in local state.");
        return;
      }
  
      if (!website.dockerRepoUrl) {
        alert("No Docker Repo URL found for this website.");
        return;
      }
  
      // 2) Parse the slug from dockerRepoUrl
      //    e.g. "https://bitbucket.org/gemstoneit/my-docker-repo"
      const parts = website.dockerRepoUrl.split("/");
      const repoSlug = parts.pop(); // "my-docker-repo" (assuming no trailing slash)
      if (!repoSlug) {
        alert("Unable to parse Docker repo slug from dockerRepoUrl.");
        return;
      }
  
      // 3) Pick a DB name. Here we use website.testDatabaseName, but you could
      //    use website.prodDatabaseName or something else
      const dbName = website.testDatabaseName;
      if (!dbName) {
        alert("No database name found (testDatabaseName) to build Docker.");
        return;
      }
  
      // 4) Call the backend endpoint: POST /bitbucket-repos/build-docker
      await axios.post(
        `${process.env.REACT_APP_API_URL}/bitbucket-repos/${repoSlug}/build-docker`,
        {
          dbName
        },
        {
          headers: {
            Authorization: `Basic ${btoa(
              `${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`
            )}`,
          },
        }
      );
  
      alert("Docker repo built & pushed!");
    } catch (err: any) {
      console.error("Failed to build Docker repo", err);
      alert(err.response?.data?.error || err.message);
    }
  };

  // Show the "CreateBitbucketModal" in either normal or docker mode
  const handleShowCreateBitbucketModal = (dockerMode = false) => {
    setIsDockerMode(dockerMode);   // If dockerMode=true, we will use projectKey="DOC"
    setShowCreateBitbucketModal(true);
  };

  const handleCloseCreateBitbucketModal = () => {
    setShowCreateBitbucketModal(false);
  };

  // If you want a separate function for Docker:
  const handleDockerBitbucketUpdate = async (repoSlug: string) => {
    try {
      const fullBitbucketUrl = `https://bitbucket.org/gemstoneit/${repoSlug}`;
      await axios.put(
        `${process.env.REACT_APP_API_URL}/websites/${dockerWebsiteId}`,
        { dockerRepoUrl: fullBitbucketUrl },
        {
          headers: {
            Authorization: `Basic ${btoa(
              `${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`
            )}`
          }
        }
      );
      fetchWebsites();
      setShowDockerBitbucketModal(false);
    } catch (err) {
      console.error('Failed to update website Docker repo', err);
      setError('Failed to update website Docker repo');
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, type, value, checked } = e.target;
    setNewWebsiteData({ ...newWebsiteData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleTriggerDeployment = async (repoUrl: string, branch: string) => {
    if (!repoUrl) {
      alert('No repository URL found');
      return;
    }
  
    // Extract the repoName from the URL. 
    // e.g. https://bitbucket.org/gemstoneit/my-repo => "my-repo"
    const repoSlug = repoUrl.split('/').pop();
    if (!repoSlug) {
      alert('Unable to parse repository slug from gitRepoUrl.');
      return;
    }
  
    try {
      // POST /bitbucket-repos/{repoName}/trigger-deploy
      // Body: { branch: "main" or "test", commitMessage: "some message" }
      await axios.post(
        `${process.env.REACT_APP_API_URL}/bitbucket-repos/${repoSlug}/trigger-deploy`,
        {
          branch,
          commitMessage: `Trigger deployment on ${branch} branch`
        },
        {
          headers: {
            Authorization: `Basic ${btoa(
              `${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`
            )}`
          }
        }
      );
  
      alert(`Deployment triggered on ${branch} branch!`);
    } catch (err: any) {
      console.error('Failed to trigger deployment', err);
      alert(err.response?.data?.error || err.message);
    }
  };

  const handleOpenAcceptanceModal = (website: Website) => {
    const today = dateInputValue();
    const existingAcceptance = website.latestAcceptance ?? null;
    const defaults = website.acceptanceDefaults ?? null;
    const reviewUrl = existingAcceptance?.reviewUrl ?? urlFromDomain(website.testDomain || website.prodDomain);
    const productionUrl = existingAcceptance?.productionUrl ?? urlFromDomain(website.prodDomain);

    setAcceptanceWebsite(website);
    setGeneratedAcceptance(existingAcceptance);
    setGeneratedAcceptanceFingerprint(null);
    setAcceptanceHistory(existingAcceptance ? [existingAcceptance] : []);
    setAcceptanceForm({
      clientEntityType: existingAcceptance?.clientEntityType ?? '',
      signerTitle: existingAcceptance?.signerTitle ?? '',
      releaseScope: existingAcceptance?.releaseScope ?? '',
      workReferences: existingAcceptance?.workReferences ?? '',
      issuesReviewedBy: existingAcceptance?.issuesReviewedBy ?? '',
      issuesReviewDate: existingAcceptance?.issuesReviewDate ?? '',
      exclusions: existingAcceptance?.exclusions ?? '',
      knownIssuesReviewStatus: '',
      deliveryModel: acceptanceDefaultValue(defaults, 'deliveryModel', existingAcceptance?.deliveryModel ?? 'gemstone') === 'third_party'
        ? 'third_party'
        : 'gemstone',
      releaseVersion: acceptanceDefaultValue(defaults, 'releaseVersion', existingAcceptance?.releaseVersion ?? `${website.projectCode || website.name}-${today}`),
      releaseIdentifier: acceptanceDefaultValue(defaults, 'releaseIdentifier', existingAcceptance?.releaseIdentifier ?? ''),
      releaseDate: acceptanceDefaultValue(defaults, 'releaseDate', existingAcceptance?.releaseDate ?? today),
      reviewUrl: acceptanceDefaultValue(defaults, 'reviewUrl', reviewUrl),
      productionUrl: acceptanceDefaultValue(defaults, 'productionUrl', productionUrl),
      clientLegalName: acceptanceDefaultValue(defaults, 'clientLegalName', website.name),
      clientCompanyNumber: acceptanceDefaultValue(defaults, 'clientCompanyNumber', existingAcceptance?.clientCompanyNumber ?? ''),
      clientRegisteredAddress: acceptanceDefaultValue(defaults, 'clientRegisteredAddress', ''),
      clientNoticeEmail: acceptanceDefaultValue(defaults, 'clientNoticeEmail', ''),
      masterAgreementReference: acceptanceDefaultValue(defaults, 'masterAgreementReference', existingAcceptance?.masterAgreementReference ?? ''),
      thirdPartyDeveloper: acceptanceDefaultValue(defaults, 'thirdPartyDeveloper', existingAcceptance?.thirdPartyDeveloper ?? ''),
      acceptanceEvidence: acceptanceDefaultValue(defaults, 'acceptanceEvidence', existingAcceptance?.acceptanceEvidence ?? ''),
      goLiveAuthorisation: acceptanceDefaultValue(defaults, 'goLiveAuthorisation', existingAcceptance?.goLiveAuthorisation ?? ''),
      approvedGoLiveWindow: acceptanceDefaultValue(defaults, 'approvedGoLiveWindow', existingAcceptance?.approvedGoLiveWindow ?? ''),
      supportAllowance: acceptanceDefaultValue(defaults, 'supportAllowance', existingAcceptance?.supportAllowance ?? ''),
      additionalSupportRate: acceptanceDefaultValue(defaults, 'additionalSupportRate', existingAcceptance?.additionalSupportRate ?? ''),
      signerName: existingAcceptance?.signerName ?? '',
      signerEmail: existingAcceptance?.signerEmail ?? '',
      notes: acceptanceDefaultValue(defaults, 'notes', existingAcceptance?.notes ?? ''),
      knownIssues: acceptanceDefaultValue(defaults, 'knownIssues', ''),
    });
    setShowAcceptanceModal(true);

    if (website.id) {
      fetchAcceptancesForWebsite(website.id, Boolean(defaults));
    }
  };

  const handleCloseAcceptanceModal = () => {
    setShowAcceptanceModal(false);
    setAcceptanceWebsite(null);
    setGeneratedAcceptance(null);
    setGeneratedAcceptanceFingerprint(null);
    setAcceptanceHistory([]);
    setPdfPreviewError(null);
  };

  const handleAcceptanceFormChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    setAcceptanceForm(prev => ({ ...prev, [name]: value, knownIssuesReviewStatus: name === 'knownIssuesReviewStatus' ? value : '' }));
  };

  const applyAcceptanceToModal = (acceptance: WebsiteAcceptanceSummary) => {
    setGeneratedAcceptance(acceptance);
    setGeneratedAcceptanceFingerprint(null);
    setAcceptanceForm(prev => ({
      ...prev,
      clientEntityType: acceptance.clientEntityType ?? '',
      signerTitle: acceptance.signerTitle ?? '',
      releaseScope: acceptance.releaseScope ?? '',
      workReferences: acceptance.workReferences ?? '',
      issuesReviewedBy: acceptance.issuesReviewedBy ?? '',
      issuesReviewDate: acceptance.issuesReviewDate ?? '',
      knownIssuesReviewStatus: acceptance.knownIssuesReviewStatus ?? '',
      exclusions: acceptance.exclusions ?? '',
      deliveryModel: acceptance.deliveryModel === 'third_party' ? 'third_party' : 'gemstone',
      releaseVersion: acceptance.releaseVersion ?? prev.releaseVersion,
      releaseIdentifier: acceptance.releaseIdentifier ?? '',
      releaseDate: acceptance.releaseDate ?? '',
      reviewUrl: acceptance.reviewUrl ?? '',
      productionUrl: acceptance.productionUrl ?? '',
      clientLegalName: acceptance.clientLegalName ?? '',
      clientCompanyNumber: acceptance.clientCompanyNumber ?? '',
      clientRegisteredAddress: acceptance.clientRegisteredAddress ?? '',
      clientNoticeEmail: acceptance.clientNoticeEmail ?? '',
      masterAgreementReference: acceptance.masterAgreementReference ?? '',
      thirdPartyDeveloper: acceptance.thirdPartyDeveloper ?? '',
      acceptanceEvidence: acceptance.acceptanceEvidence ?? '',
      knownIssues: acceptance.knownIssues ?? '',
      goLiveAuthorisation: acceptance.goLiveAuthorisation ?? '',
      approvedGoLiveWindow: acceptance.approvedGoLiveWindow ?? '',
      supportAllowance: acceptance.supportAllowance ?? '',
      additionalSupportRate: acceptance.additionalSupportRate ?? '',
      signerName: acceptance.signerName ?? '',
      signerEmail: acceptance.signerEmail ?? '',
      notes: acceptance.notes ?? '',
    }));
  };

  const fetchAcceptancesForWebsite = async (websiteId: number, preserveFormValues = false) => {
    try {
      const response = await axios.get<WebsiteAcceptanceSummary[]>(
        `${process.env.REACT_APP_API_URL}/websites/${websiteId}/acceptances`,
        { headers: apiAuthHeaders() }
      );
      const acceptances = Array.isArray(response.data) ? response.data : [];

      setAcceptanceHistory(acceptances);

      if (acceptances.length > 0) {
        if (preserveFormValues) {
          setGeneratedAcceptance(acceptances[0]);
        } else {
          applyAcceptanceToModal(acceptances[0]);
        }
      }
    } catch (err) {
      console.error('Failed to fetch website acceptances', err);
    }
  };

  const handleAcceptanceHistoryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const acceptance = acceptanceHistory.find(item => String(item.id) === event.target.value);

    if (acceptance) {
      applyAcceptanceToModal(acceptance);
    }
  };

  const handleGenerateAcceptance = async () => {
    if (!acceptanceWebsite?.id) {
      return;
    }

    setGeneratingAcceptance(true);
    setError(null);

    try {
      const payload = generationPayloadFromForm(acceptanceForm);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/websites/${acceptanceWebsite.id}/acceptance-agreements`,
        payload,
        {
          headers: apiAuthHeaders(),
        }
      );

      const acceptanceDefaults = savedDefaultsFromPayload(payload);
      setGeneratedAcceptance(response.data);
      setGeneratedAcceptanceFingerprint(generationPayloadFingerprint(payload));
      setAcceptanceHistory(prev => [response.data, ...prev.filter(item => item.id !== response.data.id)]);
      setAcceptanceWebsite(prev => prev ? { ...prev, acceptanceDefaults } : prev);
      setWebsites(prev => prev.map(website => (
        website.id === acceptanceWebsite.id
          ? { ...website, latestAcceptance: response.data, acceptanceDefaults }
          : website
      )));
      fetchWebsites();
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Failed to generate go-live acceptance'));
    } finally {
      setGeneratingAcceptance(false);
    }
  };

  const handleSendAcceptance = async () => {
    if (!generatedAcceptance) {
      return;
    }

    if (generatedAcceptanceFingerprint !== generationPayloadFingerprint(generationPayloadFromForm(acceptanceForm))) {
      setError('The acceptance details have changed since this preview was generated. Generate a new preview before sending.');
      return;
    }

    if (!window.confirm('Send this go-live acceptance through DocuSign now? Gemstone will sign first, then the client signer below.')) {
      return;
    }

    setSendingAcceptance(true);
    setError(null);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/website-acceptances/${generatedAcceptance.id}/send-docusign`,
        {
          signerName: normaliseOptionalText(acceptanceForm.signerName),
          signerEmail: normaliseEmailInput(acceptanceForm.signerEmail),
          emailSubject: `Go-live acceptance for ${acceptanceWebsite?.name || generatedAcceptance.releaseVersion}`,
        },
        {
          headers: apiAuthHeaders(),
        }
      );

      setGeneratedAcceptance(response.data);
      setAcceptanceHistory(prev => [response.data, ...prev.filter(item => item.id !== response.data.id)]);
      fetchWebsites();
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Failed to send go-live acceptance through DocuSign'));
    } finally {
      setSendingAcceptance(false);
    }
  };
  

  const handleGenerateEnvForTest = async (websiteId: number) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/plesk/test-env/${websiteId}`,
        {},
        {
          headers: {
            Authorization: `Basic ${btoa(
              `${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`
            )}`,
          },
        }
      );
      alert("Env and .htaccess generated for the test subdomain!");
    } catch (err: any) {
      alert(err.response?.data?.error || err.message);
    }
  };

  /**
   * 1) Prompt the user, update the website record with `testDomain`
   */
  const handleSetSubdomain = async (websiteId: number) => {
    const subdomain = prompt("Enter the subdomain name (e.g., test123):");
    if (!subdomain) return;

    try {
      // Suppose our DB column is `testDomain`
      // So we update the website with `testDomain` = "test123.gem.li"
      const subdomainFull = `${subdomain}.gem.li`;

      await axios.put(
        `${process.env.REACT_APP_API_URL}/websites/${websiteId}`,
        { testDomain: subdomainFull },
        {
          headers: {
            Authorization: `Basic ${btoa(
              `${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`
            )}`,
          },
        }
      );

      alert(`Subdomain set to ${subdomainFull} in DB!`);
      fetchWebsites(); // refresh local data
    } catch (err: any) {
      alert(`Failed to set subdomain: ${err.response?.data?.error || err.message}`);
    }
  };

  /**
   * 2) Actually create the subdomain in Plesk (only if subdomain is non-null)
   */
  const handleCreateSubdomain = async (websiteId: number) => {
    // find the website object
    const website = websites.find((w) => w.id === websiteId);
    if (!website) {
      alert("Website not found");
      return;
    }

    if (!website.testDomain) {
      alert("No subdomain set on this website record yet!");
      return;
    }

    // For example, if testDomain is "test123.gem.li", we only pass the sub-part "test123"
    // Because your createSubdomain() method in PleskService uses <parent>gem.li</parent>.
    const subdomainPart = website.testDomain.replace(".gem.li", "");

    try {
      // Call your new Plesk endpoint: POST /plesk/subdomains
      // with body { subdomainName: "test123" }
      const resp = await axios.post(
        `${process.env.REACT_APP_API_URL}/plesk/subdomains`,
        { subdomainName: subdomainPart },
        {
          headers: {
            Authorization: `Basic ${btoa(
              `${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`
            )}`,
          },
        }
      );

      alert(resp.data.message || `Subdomain ${website.testDomain} created in Plesk!`);
    } catch (err: any) {
      alert(`Failed to create subdomain: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleGenerateEnv = async (
    subscriptionId: number,
    websiteId: number,
    dbName: string,
    dbUser: string,
    dbPass: string
  ) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/plesk/subscriptions/${subscriptionId}/generate-env`,
        {
          websiteId,
          dbName,
          dbUser,
          dbPass,
        },
        {
          headers: {
            Authorization: `Basic ${btoa(
              `${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`
            )}`,
          },
        }
      );

      alert('Env and .htaccess generated on remote server!');
    } catch (err: any) {
      alert(err.response?.data?.error || err.message);
    }
  };

  /**
   * 3) Create the 6 environment variables on CircleCI for a given website
   */
  const handleCreateCircleCIEnvVars = async (websiteId: number) => {
    try {
      // 1) Find the website object in your local state
      const website = websites.find((w) => w.id === websiteId);
      if (!website) {
        alert("Website not found");
        return;
      }

      if (!website.gitRepoUrl) {
        alert("No gitRepoUrl found on this website.");
        return;
      }

      // 2) Parse the repository slug from website.gitRepoUrl
      //    e.g. "https://bitbucket.org/gemstoneit/my-bitbucket-repo"
      const parts = website.gitRepoUrl.split("/");
      const repoSlug = parts.pop(); // "my-bitbucket-repo"
      const org = parts.pop();      // "gemstoneit"
      if (!repoSlug || !org) {
        alert("Unable to parse workspace or repository slug from gitRepoUrl.");
        return;
      }

      // CircleCI expects a projectSlug in the form "bitbucket/org/repo"
      // or "gh/org/repo" if GitHub. For Bitbucket, do:
      const projectSlug = `bitbucket/${org}/${repoSlug}`;

      // 3) Construct the 6 environment variables
      // For example:
      //   DEPLOY_SERVER_HOSTNAME_PROD = "gemstoneit.co.uk"
      //   DEPLOY_SERVER_USERNAME_PROD = "root"
      //   DEPLOY_SERVER_PROJECT_PATH_PROD = `/var/www/vhosts/${website.prodDomain}/httpdocs`
      //   DEPLOY_SERVER_HOSTNAME_TEST = "gemstoneit.co.uk"
      //   DEPLOY_SERVER_USERNAME_TEST = "root"
      //   DEPLOY_SERVER_PROJECT_PATH_TEST = `/var/www/vhosts/gem.li/${website.testDomain}`
      const envVars = [
        {
          name: "DEPLOY_SERVER_HOSTNAME_PROD",
          value: "gemstoneit.co.uk",
        },
        {
          name: "DEPLOY_SERVER_USERNAME_PROD",
          value: "root",
        },
        {
          name: "DEPLOY_SERVER_PROJECT_PATH_PROD",
          value: `/var/www/vhosts/${website.prodDomain}/httpdocs`,
        },
        {
          name: "DEPLOY_SERVER_HOSTNAME_TEST",
          value: "gemstoneit.co.uk",
        },
        {
          name: "DEPLOY_SERVER_USERNAME_TEST",
          value: "root",
        },
        {
          name: "DEPLOY_SERVER_PROJECT_PATH_TEST",
          value: `/var/www/vhosts/gem.li/${website.testDomain}`,
        },
      ];

      // 4) Call your API endpoint for each env var
      // e.g. POST /circleci/projects/{projectSlug}/envvar
      // with { name, value }
      for (const envVar of envVars) {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/circleci/projects/${projectSlug}/envvar`,
          {
            name: envVar.name,
            value: envVar.value,
          },
          {
            headers: {
              Authorization: `Basic ${btoa(
                `${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`
              )}`,
            },
          }
        );
      }

      alert("All 6 environment variables created in CircleCI!");
    } catch (err: any) {
      console.error("Failed to create CircleCI environment variables", err);
      alert(err.response?.data?.error || err.message);
    }
  };

  /**
   * Install WordPress on either the Prod or Test environment.
   * We'll pass { environment: 'prod' | 'test' } to the API endpoint.
   */
  const handleInstallWordPress = async (websiteId: number, environment: 'prod' | 'test') => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/websites/${websiteId}/install-wordpress`,
        { environment }, // sending "prod" or "test" in the JSON body
        {
          headers: {
            Authorization: `Basic ${btoa(
              `${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`
            )}`,
          },
        }
      );

      alert(`WordPress installed successfully in ${environment} environment!`);
    } catch (err: any) {
      console.error(`Failed to install WP (${environment})`, err);
      alert(err.response?.data?.error || err.message);
    }
  };


  /**
   * 1) Commit a default CircleCI config file to .circleci/config.yml in the repo
   */
  const handleCommitCircleCIConfig = async (websiteId: number) => {
    try {
      // 1) Find the website object
      const website = websites.find((w) => w.id === websiteId);
      if (!website) {
        alert("Website not found");
        return;
      }
  
      if (!website.gitRepoUrl) {
        alert("No gitRepoUrl found on this website.");
        return;
      }
  
      // Example repoUrl: "https://bitbucket.org/gemstoneit/my-bitbucket-repo"
      const parts = website.gitRepoUrl.split("/");
      const repoSlug = parts.pop(); // "my-bitbucket-repo"
      if (!repoSlug) {
        alert("Unable to parse repository slug from gitRepoUrl.");
        return;
      }
  
      // 2) Call your CircleCIController endpoint to get the generated config
      //    POST /circleci/deployment-script
      //    It returns JSON with { "circleci_config": "..." }
      const scriptResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/circleci/deployment-script`,
        {}, // no request body needed unless you want to pass domain or other variables
        {
          headers: {
            Authorization: `Basic ${btoa(
              `${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`
            )}`,
          },
        }
      );
  
      // Extract the YAML from scriptResponse
      const circleCiConfig = scriptResponse.data.circleci_config;
      if (!circleCiConfig) {
        alert("No CircleCI config returned from the /circleci/deployment-script endpoint.");
        return;
      }
  
      // 3) Commit that config to .circleci/config.yml in the Bitbucket repo
      await axios.post(
        `${process.env.REACT_APP_API_URL}/bitbucket-repos/${repoSlug}/commit-file`,
        {
          relativeFilePath: ".circleci/config.yml",
          content: circleCiConfig,
          commitMessage: "Add/Update CircleCI deployment config",
        },
        {
          headers: {
            Authorization: `Basic ${btoa(
              `${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`
            )}`,
          },
        }
      );
  
      alert("CircleCI config committed successfully!");
    } catch (err: any) {
      console.error("Failed to commit CircleCI config", err);
      alert(err.response?.data?.error || err.message);
    }
  };

  /**
   * 2) Create a project on CircleCI for the repo
   */
  const handleCreateCircleCIProject = async (websiteId: number) => {
    try {
      // Find the website object
      const website = websites.find((w) => w.id === websiteId);
      if (!website) {
        alert("Website not found");
        return;
      }

      if (!website.gitRepoUrl) {
        alert("No gitRepoUrl found on this website.");
        return;
      }

      // Example: "https://bitbucket.org/gemstoneit/my-bitbucket-repo"
      const parts = website.gitRepoUrl.split("/");
      const repoSlug = parts.pop(); // "my-bitbucket-repo"
      const org = parts.pop();      // "gemstoneit" (Bitbucket workspace)
      if (!repoSlug || !org) {
        alert("Unable to parse workspace or repository slug from gitRepoUrl.");
        return;
      }

      // Call your endpoint:
      //    POST /circleci/projects/bitbucket/{org}/{repoSlug}
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/circleci/projects/bitbucket/${org}/${repoSlug}`,
        {},
        {
          headers: {
            Authorization: `Basic ${btoa(
              `${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`
            )}`,
          },
        }
      );

      alert("CircleCI project created! Check advanced settings in the response.");
      console.log("CircleCI project response:", response.data);

    } catch (err: any) {
      console.error("Failed to create CircleCI project", err);
      alert(err.response?.data?.error || err.message);
    }
  };


  // 1) Production DB creation
  const handleCreateProdDb = async (websiteId: number, subscriptionId: number) => {
    const baseName = prompt("Enter a base name for the production DB (will get 'prod_' prefix):");
    if (!baseName) return;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/plesk/subscriptions/${subscriptionId}/databases/prod`,
        { name: baseName },
        {
          headers: {
            Authorization: `Basic ${btoa(`${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`)}`
          }
        }
      );
      // e.g. response.data => { databaseId, dbName, dbUser, dbPassword }
      const { dbName, dbUser, dbPassword } = response.data;

      // Update the website with these fields
      await axios.put(
        `${process.env.REACT_APP_API_URL}/websites/${websiteId}`,
        {
          prodDatabaseName: dbName,
          prodDatabaseUser: dbUser,
          prodDatabasePass: dbPassword
        },
        {
          headers: {
            Authorization: `Basic ${btoa(`${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`)}`
          }
        }
      );

      alert(`Production DB created! DB name: ${dbName}, user: ${dbUser}, pass: ${dbPassword}`);
      fetchWebsites();

    } catch (err: any) {
      alert(`Failed to create production DB: ${err.response?.data?.error || err.message}`);
    }
  };

  // 2) Test DB creation
  const handleCreateTestDb = async (websiteId: number, subscriptionId: number) => {
    const baseName = prompt("Enter a base name for the test DB (will get 'test_' prefix):");
    if (!baseName) return;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/plesk/subscriptions/${subscriptionId}/databases/test`,
        { name: baseName },
        {
          headers: {
            Authorization: `Basic ${btoa(`${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`)}`
          }
        }
      );
      const { dbName, dbUser, dbPassword } = response.data;

      await axios.put(
        `${process.env.REACT_APP_API_URL}/websites/${websiteId}`,
        {
          testDatabaseName: dbName,
          testDatabaseUser: dbUser,
          testDatabasePass: dbPassword
        },
        {
          headers: {
            Authorization: `Basic ${btoa(`${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`)}`
          }
        }
      );

      alert(`Test DB created! DB name: ${dbName}, user: ${dbUser}, pass: ${dbPassword}`);
      fetchWebsites();

    } catch (err: any) {
      alert(`Failed to create test DB: ${err.response?.data?.error || err.message}`);
    }
  };


  const handleInstallBedrock = async (repoUrl: string) => {
    if (!repoUrl) {
      alert('No repository URL found');
      return;
    }

    // Extract the slug from the end of the URL
    const repoSlug = repoUrl.split('/').pop();
    if (!repoSlug) {
      alert('No repository slug found from URL');
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/bitbucket-repos/${repoSlug}/install-bedrock`,
        {},
        {
          headers: {
            Authorization: `Basic ${btoa(
              `${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`
            )}`
          }
        }
      );
      alert('Bedrock installed successfully!');
    } catch (err: any) {
      console.error('Failed to install Bedrock', err);
      alert(err.response?.data?.error || 'Install Bedrock failed');
    }
  };

  const handleSubmitNewWebsite = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/websites`, newWebsiteData, {
        headers: {
          'Authorization': `Basic ${btoa(
            `${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`
          )}`
        }
      });
      fetchWebsites();
      handleCloseModal();
    } catch (err) {
      console.error('Failed to create website', err);
      setError('Failed to create website');
    }
  };

  // Customer 
  const handleCustomerSelect = (websiteId: number) => {
    setCurrentWebsiteId(websiteId);
    setShowCustomerModal(true);
  };
  const handleCustomerUpdate = async (customerId: number) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/websites/${currentWebsiteId}`,
        { pleskCustomerId: customerId },
        {
          headers: {
            'Authorization': `Basic ${btoa(
              `${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`
            )}`
          }
        }
      );
      fetchWebsites();
      setShowCustomerModal(false);
    } catch (err) {
      console.error('Failed to update website', err);
      setError('Failed to update website');
    }
  };
  const handleShowCreateCustomerModal = () => setShowCreateCustomerModal(true);
  const handleCloseCreateCustomerModal = () => setShowCreateCustomerModal(false);

  const handleCustomerFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewCustomerData({ ...newCustomerData, [name]: value });
  };

  const handleCreateCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/plesk/customers`,
        newCustomerData,
        {
          headers: {
            'Authorization': `Basic ${btoa(
              `${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`
            )}`
          }
        }
      );
      const newCustomerId = response.data.id;
      if (currentWebsiteId) {
        await handleCustomerUpdate(newCustomerId);
      }
      fetchCustomers();
      setShowCreateCustomerModal(false);
      setShowCustomerModal(false);
    } catch (err) {
      console.error('Failed to create customer', err);
      setError('Failed to create customer');
    }
  };

  // Subscriptions
  const handleSubscriptionSelect = (websiteId: number, pleskCustomerId: number) => {
    setCurrentWebsiteId(websiteId);
    setCurrentCustomerId(pleskCustomerId);
    fetchSubscriptions(pleskCustomerId);
    setShowSubscriptionModal(true);
  };
  const handleSubscriptionUpdate = async (subscriptionId: number) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/websites/${currentWebsiteId}`,
        { pleskSubscription: subscriptionId },
        {
          headers: {
            'Authorization': `Basic ${btoa(
              `${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`
            )}`
          }
        }
      );
      fetchWebsites();
      setShowSubscriptionModal(false);
    } catch (err) {
      console.error('Failed to update website subscription', err);
      setError('Failed to update website subscription');
    }
  };
  const handleShowCreateSubscriptionModal = () => setShowCreateSubscriptionModal(true);
  const handleCloseCreateSubscriptionModal = () => setShowCreateSubscriptionModal(false);

  const handleSubscriptionFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewSubscriptionData({ ...newSubscriptionData, [name]: value });
  };

  const handleCreateSubscription = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/plesk/customers/${currentCustomerId}/plesk-subscriptions`,
        newSubscriptionData,
        {
          headers: {
            'Authorization': `Basic ${btoa(
              `${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`
            )}`
          }
        }
      );
      const newSubscriptionId = response.data.subscriptionId;
      await handleSubscriptionUpdate(newSubscriptionId);
      setShowCreateSubscriptionModal(false);
      setShowSubscriptionModal(false);
    } catch (err) {
      console.error('Failed to create subscription', err);
      setError('Failed to create subscription');
    }
  };

  // Bitbucket
  const handleBitbucketSelect = (websiteId: number) => {
    setCurrentWebsiteId(websiteId);
    fetchBitbucketRepos(); // triggers bitbucketReposLoading = true
    setShowBitbucketModal(true);
  };

  const handleDockerBitbucketSelect = (websiteId: number) => {
    setDockerWebsiteId(websiteId);
    fetchBitbucketRepos();
    setShowDockerBitbucketModal(true);  // open the Docker "BitbucketSelectModal"
  };

  const handleBitbucketUpdate = async (repoSlug: string) => {
    try {
      const fullBitbucketUrl = `https://bitbucket.org/gemstoneit/${repoSlug}`;
      await axios.put(
        `${process.env.REACT_APP_API_URL}/websites/${currentWebsiteId}`,
        { gitRepoUrl: fullBitbucketUrl },
        {
          headers: {
            Authorization: `Basic ${btoa(
              `${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`
            )}`
          }
        }
      );
      fetchWebsites();
      setShowBitbucketModal(false);
    } catch (err) {
      console.error('Failed to update website bitbucket repo', err);
      setError('Failed to update website with bitbucket repo');
    }
  };

  const handleDailyUpdateExclusionChange = async (websiteId: number, excluded: boolean) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/websites/${websiteId}`,
        { excludeFromDailyUpdates: excluded },
        { headers: apiAuthHeaders() }
      );

      setWebsites(previous => previous.map(website => (
        website.id === websiteId
          ? {
              ...website,
              excludeFromDailyUpdates: Boolean(response.data.excludeFromDailyUpdates),
            }
          : website
      )));
    } catch (err: any) {
      throw new Error(apiErrorMessage(err, 'Failed to update the daily Autocomposer setting'));
    }
  };

  const handleInternalStatusChange = async (websiteId: number, isInternal: boolean) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/websites/${websiteId}`,
        { isInternal },
        { headers: apiAuthHeaders() }
      );

      setWebsites(previous => previous.map(website => (
        website.id === websiteId
          ? {
              ...website,
              isInternal: Boolean(response.data.isInternal),
            }
          : website
      )));
    } catch (err: any) {
      throw new Error(apiErrorMessage(err, 'Failed to update the internal-site setting'));
    }
  };

  const handleCreateBitbucketRepo = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setCreatingRepoLoading(true);
  
      // If isDockerMode, force projectKey="DOC". Otherwise, default to "WOR".
      const projectKeyToUse = isDockerMode ? "DOC" : "WOR";
  
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/bitbucket-repos`,
        {
          name: newBitbucketRepo.name,
          projectKey: projectKeyToUse,
          isPrivate: true
        },
        {
          headers: {
            Authorization: `Basic ${btoa(
              `${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`
            )}`
          }
        }
      );
  
      const createdSlug = response.data.slug;
  
      // If it's Docker mode, call handleDockerBitbucketUpdate
      // Otherwise call handleBitbucketUpdate
      if (isDockerMode) {
        await handleDockerBitbucketUpdate(createdSlug);
      } else {
        await handleBitbucketUpdate(createdSlug);
      }
  
      setShowCreateBitbucketModal(false);
      // If you still have a separate "BitbucketSelectModal", close it if open
      setShowBitbucketModal(false);
      // If you had a separate docker modal, also close it
      setShowDockerBitbucketModal(false);
    } catch (err) {
      console.error('Failed to create bitbucket repo', err);
      setError('Failed to create bitbucket repo');
    } finally {
      setCreatingRepoLoading(false); // <--- done creating
    }
  };

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const filteredWebsites = [...websites]
    .sort((left, right) => left.name.localeCompare(right.name))
    .filter((website) => {
      if (showNeedsAcceptanceOnly && !acceptanceNeedsAttention(website)) {
        return false;
      }

      if (!normalizedSearchTerm) {
        return true;
      }

      const haystack = [
        website.name,
        website.projectCode,
        website.prodDomain,
        website.testDomain,
        website.gitRepoUrl,
        website.type,
        website.latestAcceptance?.status,
        website.latestAcceptance?.releaseVersion,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(normalizedSearchTerm);
    });

  const websitesWithRepo = websites.filter((website) => Boolean(website.gitRepoUrl)).length;
  const websitesWithTestDomain = websites.filter((website) => Boolean(website.testDomain)).length;
  const websitesNeedingAttention = websites.filter((website) => Boolean(website.prodDomainError)).length;
  const websitesNeedingAcceptance = websites.filter(acceptanceNeedsAttention).length;
  const generatedAcceptanceDocuments = acceptanceDocuments(generatedAcceptance);
  const generatedAcceptancePdf = pdfDocumentForAcceptance(generatedAcceptance);
  const generatedAcceptanceSent = hasSentAcceptance(generatedAcceptance);
  const isThirdPartyAcceptance = acceptanceForm.deliveryModel === 'third_party';
  const currentAcceptanceFingerprint = generationPayloadFingerprint(generationPayloadFromForm(acceptanceForm));
  const acceptancePreviewStale = Boolean(
    generatedAcceptance && generatedAcceptanceFingerprint !== currentAcceptanceFingerprint
  );
  const commonAcceptanceFieldsComplete = Boolean(
    normaliseOptionalText(acceptanceForm.releaseVersion) &&
    normaliseOptionalText(acceptanceForm.reviewUrl) &&
    normaliseOptionalText(acceptanceForm.clientLegalName) &&
    normaliseOptionalText(acceptanceForm.clientRegisteredAddress) &&
    normaliseOptionalText(acceptanceForm.masterAgreementReference) &&
    normaliseOptionalText(acceptanceForm.clientEntityType) &&
    (acceptanceForm.clientEntityType !== 'company' || normaliseOptionalText(acceptanceForm.clientCompanyNumber)) &&
    normaliseEmailInput(acceptanceForm.clientNoticeEmail) &&
    normaliseOptionalText(acceptanceForm.signerName) && normaliseEmailInput(acceptanceForm.signerEmail) && normaliseOptionalText(acceptanceForm.signerTitle) &&
    normaliseOptionalText(acceptanceForm.releaseIdentifier) && normaliseOptionalText(acceptanceForm.productionUrl) &&
    normaliseOptionalText(acceptanceForm.acceptanceEvidence) && normaliseOptionalText(acceptanceForm.goLiveAuthorisation) && normaliseOptionalText(acceptanceForm.approvedGoLiveWindow) &&
    normaliseOptionalText(acceptanceForm.releaseScope) && normaliseOptionalText(acceptanceForm.workReferences) &&
    normaliseOptionalText(acceptanceForm.knownIssues) && normaliseOptionalText(acceptanceForm.exclusions) &&
    normaliseOptionalText(acceptanceForm.issuesReviewedBy) && normaliseOptionalText(acceptanceForm.issuesReviewDate) && acceptanceForm.knownIssuesReviewStatus === 'confirmed'
  );
  const thirdPartyAcceptanceFieldsComplete = !isThirdPartyAcceptance || Boolean(
    normaliseOptionalText(acceptanceForm.releaseIdentifier) &&
    normaliseOptionalText(acceptanceForm.productionUrl) &&
    normaliseOptionalText(acceptanceForm.thirdPartyDeveloper) &&
    normaliseOptionalText(acceptanceForm.acceptanceEvidence) &&
    normaliseOptionalText(acceptanceForm.knownIssues) &&
    normaliseOptionalText(acceptanceForm.goLiveAuthorisation) &&
    normaliseOptionalText(acceptanceForm.approvedGoLiveWindow) &&
    normaliseOptionalText(acceptanceForm.supportAllowance) &&
    normaliseOptionalText(acceptanceForm.additionalSupportRate)
  );
  const canGenerateAcceptance = commonAcceptanceFieldsComplete && thirdPartyAcceptanceFieldsComplete;
  const canSendAcceptance = Boolean(
    generatedAcceptance &&
    !generatedAcceptanceSent &&
    !acceptancePreviewStale &&
    normaliseOptionalText(acceptanceForm.signerName) &&
    normaliseEmailInput(acceptanceForm.signerEmail)
  );

  return (
    <div className="website-manager-page">
      <div className="container py-4 py-lg-5">
        <section className="website-manager-hero">
          <div>
            <p className="website-manager-hero__eyebrow">Operations Console</p>
            <h1>Website Manager</h1>
            <p className="website-manager-hero__copy">
              Track live domains, test installs, repositories, and deployment setup without
              fighting a horizontal spreadsheet.
            </p>
          </div>
          <Button variant="light" onClick={handleShowModal}>
            Create Website
          </Button>
        </section>

        <Row className="g-3 website-manager-stats">
          <Col sm={6} xl={3}>
            <Card className="website-manager-stat border-0 shadow-sm">
              <Card.Body>
                <span>Total Sites</span>
                <strong>{websites.length}</strong>
              </Card.Body>
            </Card>
          </Col>
          <Col sm={6} xl={3}>
            <Card className="website-manager-stat border-0 shadow-sm">
              <Card.Body>
                <span>Repos Linked</span>
                <strong>{websitesWithRepo}</strong>
              </Card.Body>
            </Card>
          </Col>
          <Col sm={6} xl={3}>
            <Card className="website-manager-stat border-0 shadow-sm">
              <Card.Body>
                <span>Test Domains</span>
                <strong>{websitesWithTestDomain}</strong>
              </Card.Body>
            </Card>
          </Col>
          <Col sm={6} xl={3}>
            <Card className="website-manager-stat border-0 shadow-sm">
              <Card.Body>
                <span>Needs Attention</span>
                <strong>{websitesNeedingAttention}</strong>
              </Card.Body>
            </Card>
          </Col>
          <Col sm={6} xl={3}>
            <Card className="website-manager-stat border-0 shadow-sm">
              <Card.Body>
                <span>Needs Acceptance</span>
                <strong>{websitesNeedingAcceptance}</strong>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <section className="website-manager-toolbar">
          <div>
            <h2>Site Inventory</h2>
            <p className="mb-0 text-muted">
              Showing {filteredWebsites.length} of {websites.length} websites.
            </p>
          </div>
          <Form.Control
            className="website-manager-search"
            placeholder="Search by name, code, domain, repo, or type"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
          <Button
            variant={showNeedsAcceptanceOnly ? 'primary' : 'outline-primary'}
            onClick={() => setShowNeedsAcceptanceOnly(prev => !prev)}
          >
            Needs Acceptance
          </Button>
        </section>

        {error && <Alert variant="danger">{error}</Alert>}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : (
          <WebsiteTable
            handleStoreIdentitySave={async (websiteId, values) => {
              const response = await axios.put<Website>(
                `${process.env.REACT_APP_API_URL}/websites/${websiteId}`, values,
                { headers: apiAuthHeaders() },
              );
              setWebsites(previous => previous.map(website => website.id === websiteId ? response.data : website));
            }}
            websites={filteredWebsites}
            handleCustomerSelect={handleCustomerSelect}
            handleSubscriptionSelect={handleSubscriptionSelect}
            handleBitbucketSelect={handleBitbucketSelect}
            handleInstallBedrock={handleInstallBedrock}
            handleCreateProdDb={handleCreateProdDb}
            handleCreateTestDb={handleCreateTestDb}
            handleGenerateEnv={handleGenerateEnv}
            handleCommitCircleCIConfig={handleCommitCircleCIConfig}
            handleCreateCircleCIProject={handleCreateCircleCIProject}
            handleCreateCircleCIEnvVars={handleCreateCircleCIEnvVars}
            handleInstallWordPress={handleInstallWordPress}
            handleSetSubdomain={handleSetSubdomain}
            handleCreateSubdomain={handleCreateSubdomain}
            handleGenerateEnvForTest={handleGenerateEnvForTest}
            handleTriggerDeployment={handleTriggerDeployment}
            handleDockerBitbucketSelect={handleDockerBitbucketSelect}
            handleBuildDocker={handleBuildDocker}
            handleGenerateAcceptance={handleOpenAcceptanceModal}
            handleDailyUpdateExclusionChange={handleDailyUpdateExclusionChange}
            handleInternalStatusChange={handleInternalStatusChange}
          />
        )}
      </div>

      {/* Modals */}
      <CreateWebsiteModal
        show={showModal}
        handleClose={handleCloseModal}
        handleSubmit={handleSubmitNewWebsite}
        formData={newWebsiteData}
        handleFormChange={handleFormChange}
      />

      <CustomerSelectModal
        show={showCustomerModal}
        onHide={() => setShowCustomerModal(false)}
        customers={customers}
        handleCustomerUpdate={handleCustomerUpdate}
        handleShowCreateCustomerModal={handleShowCreateCustomerModal}
      />

      <CreateCustomerModal
        show={showCreateCustomerModal}
        onHide={handleCloseCreateCustomerModal}
        formData={newCustomerData}
        handleFormChange={handleCustomerFormChange}
        handleSubmit={handleCreateCustomer}
      />

      <SubscriptionSelectModal
        show={showSubscriptionModal}
        onHide={() => setShowSubscriptionModal(false)}
        subscriptions={subscriptions}
        handleSubscriptionUpdate={handleSubscriptionUpdate}
        handleShowCreateSubscriptionModal={handleShowCreateSubscriptionModal}
      />

      <CreateSubscriptionModal
        show={showCreateSubscriptionModal}
        onHide={handleCloseCreateSubscriptionModal}
        formData={newSubscriptionData}
        handleFormChange={handleSubscriptionFormChange}
        handleSubmit={handleCreateSubscription}
      />

      {/* Normal Bitbucket modal */}
      <BitbucketSelectModal
        show={showBitbucketModal}                       // tied to the normal “Edit Repo” button
        onHide={() => setShowBitbucketModal(false)}
        bitbucketRepos={bitbucketRepos}
        handleBitbucketUpdate={handleBitbucketUpdate}   // updates gitRepoUrl
        handleShowCreateBitbucketModal={() => handleShowCreateBitbucketModal(false)}
        bitbucketReposLoading={bitbucketReposLoading}
      />

      {/* Docker Bitbucket modal */}
      <BitbucketSelectModal
        show={showDockerBitbucketModal}                 // tied to the “Edit Docker Repo” button
        onHide={() => setShowDockerBitbucketModal(false)}
        bitbucketRepos={bitbucketRepos}
        handleBitbucketUpdate={handleDockerBitbucketUpdate} // updates dockerRepoUrl
        handleShowCreateBitbucketModal={() => handleShowCreateBitbucketModal(true)}
        bitbucketReposLoading={bitbucketReposLoading}
      />

      <Modal show={showAcceptanceModal} onHide={handleCloseAcceptanceModal} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Generate Go-Live Acceptance</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {acceptanceHistory.length > 0 && (
            <Form.Group className="mb-3">
              <Form.Label>Generated Version</Form.Label>
              <Form.Select
                value={generatedAcceptance?.id ?? ''}
                onChange={handleAcceptanceHistoryChange}
                aria-label="Generated acceptance versions"
              >
                {acceptanceHistory.map(acceptance => (
                  <option key={acceptance.id} value={acceptance.id}>
                    {acceptanceHistoryLabel(acceptance)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          {generatedAcceptance && (
            <Alert variant={generatedAcceptanceSent ? 'info' : 'success'}>
              <div>
                <strong>{generatedAcceptance.releaseVersion}</strong>
                {' '}
                {generatedAcceptanceSent ? 'has been sent.' : 'preview generated.'}
              </div>
              <div className="d-flex flex-wrap gap-2 mt-2">
                {generatedAcceptanceDocuments.map(document => (
                  document.sharepointWebUrl ? (
                    <Button key={`${document.type}-${document.id}`} size="sm" variant="outline-success" href={document.sharepointWebUrl} target="_blank" rel="noopener noreferrer">
                      Open {document.type.toUpperCase()}
                    </Button>
                  ) : null
                ))}
              </div>
              {(generatedAcceptance.docusignEnvelope || generatedAcceptance.contractGeneration?.docusignEnvelope) && (
                <div className="mt-2">
                  DocuSign: {(generatedAcceptance.docusignEnvelope ?? generatedAcceptance.contractGeneration?.docusignEnvelope)?.status}
                </div>
              )}
            </Alert>
          )}

          {acceptancePreviewStale && !generatedAcceptanceSent && (
            <Alert variant="warning">
              Generate a new preview before sending. The current form does not match the selected document.
            </Alert>
          )}

          {generatedAcceptance && (
            <div className="acceptance-preview-panel mb-3">
              <div className="acceptance-preview-panel__header">
                <strong>PDF Preview</strong>
                {generatedAcceptancePdf?.sharepointWebUrl && (
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    href={generatedAcceptancePdf.sharepointWebUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open PDF
                  </Button>
                )}
              </div>
              {pdfPreviewLoading && (
                <div className="acceptance-preview-panel__loading">
                  <Spinner animation="border" size="sm" />
                </div>
              )}
              {!pdfPreviewLoading && pdfPreviewUrl && (
                <iframe
                  className="acceptance-preview-panel__frame"
                  src={pdfPreviewUrl}
                  title="Go-live acceptance PDF preview"
                />
              )}
              {!pdfPreviewLoading && !pdfPreviewUrl && (
                <Alert variant={pdfPreviewError ? 'warning' : 'secondary'} className="mb-0">
                  {pdfPreviewError ?? 'No generated PDF is available for this acceptance.'}
                </Alert>
              )}
            </div>
          )}

          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Delivery Model</Form.Label>
              <div>
                <ButtonGroup aria-label="Website delivery model">
                  <ToggleButton
                    id="acceptance-delivery-gemstone"
                    type="radio"
                    variant="outline-primary"
                    name="deliveryModel"
                    value="gemstone"
                    checked={acceptanceForm.deliveryModel === 'gemstone'}
                    onChange={() => setAcceptanceForm(prev => ({ ...prev, deliveryModel: 'gemstone', knownIssuesReviewStatus: '' }))}
                  >
                    Gemstone-built
                  </ToggleButton>
                  <ToggleButton
                    id="acceptance-delivery-third-party"
                    type="radio"
                    variant="outline-primary"
                    name="deliveryModel"
                    value="third_party"
                    checked={acceptanceForm.deliveryModel === 'third_party'}
                    onChange={() => setAcceptanceForm(prev => ({ ...prev, deliveryModel: 'third_party', knownIssuesReviewStatus: '' }))}
                  >
                    Third-party build
                  </ToggleButton>
                </ButtonGroup>
              </div>
            </Form.Group>

            <div className="row">
              <Form.Group className="mb-3 col-md-3">
                <Form.Label>Website</Form.Label>
                <Form.Control value={acceptanceWebsite?.name ?? ''} disabled />
              </Form.Group>
              <Form.Group className="mb-3 col-md-3">
                <Form.Label>Release Version *</Form.Label>
                <Form.Control name="releaseVersion" value={acceptanceForm.releaseVersion} onChange={handleAcceptanceFormChange} />
              </Form.Group>
              <Form.Group className="mb-3 col-md-3">
                <Form.Label>Commit, Tag or Build {isThirdPartyAcceptance ? '*' : ''}</Form.Label>
                <Form.Control name="releaseIdentifier" value={acceptanceForm.releaseIdentifier} onChange={handleAcceptanceFormChange} />
              </Form.Group>
              <Form.Group className="mb-3 col-md-3">
                <Form.Label>Release Date</Form.Label>
                <Form.Control type="date" name="releaseDate" value={acceptanceForm.releaseDate} onChange={handleAcceptanceFormChange} />
              </Form.Group>
            </div>

            <div className="row">
              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Review URL *</Form.Label>
                <Form.Control type="url" name="reviewUrl" value={acceptanceForm.reviewUrl} onChange={handleAcceptanceFormChange} />
              </Form.Group>
              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Production URL {isThirdPartyAcceptance ? '*' : ''}</Form.Label>
                <Form.Control type="url" name="productionUrl" value={acceptanceForm.productionUrl} onChange={handleAcceptanceFormChange} />
              </Form.Group>
            </div>

            <div className="row">
              <Form.Group className="mb-3 col-md-5">
                <Form.Label>Client Legal Name *</Form.Label>
                <Form.Control name="clientLegalName" value={acceptanceForm.clientLegalName} onChange={handleAcceptanceFormChange} />
              </Form.Group>
              <Form.Group className="mb-3 col-md-3">
                <Form.Label>Company Number</Form.Label>
                <Form.Control name="clientCompanyNumber" value={acceptanceForm.clientCompanyNumber} onChange={handleAcceptanceFormChange} />
              </Form.Group>
              <Form.Group className="mb-3 col-md-4">
                <Form.Label>Client Notice Email</Form.Label>
                <Form.Control type="email" name="clientNoticeEmail" value={acceptanceForm.clientNoticeEmail} onChange={handleAcceptanceFormChange} />
              </Form.Group>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Client Registered Address *</Form.Label>
              <Form.Control as="textarea" rows={2} name="clientRegisteredAddress" value={acceptanceForm.clientRegisteredAddress} onChange={handleAcceptanceFormChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Master Agreement Reference *</Form.Label>
              <Form.Control name="masterAgreementReference" value={acceptanceForm.masterAgreementReference} onChange={handleAcceptanceFormChange} />
            </Form.Group>

            {isThirdPartyAcceptance && (
              <div className="row">
                <Form.Group className="mb-3 col-md-6">
                  <Form.Label>Third-Party Developer *</Form.Label>
                  <Form.Control name="thirdPartyDeveloper" value={acceptanceForm.thirdPartyDeveloper} onChange={handleAcceptanceFormChange} />
                </Form.Group>
                <Form.Group className="mb-3 col-md-6">
                  <Form.Label>Approved Go-Live Window *</Form.Label>
                  <Form.Control name="approvedGoLiveWindow" value={acceptanceForm.approvedGoLiveWindow} onChange={handleAcceptanceFormChange} />
                </Form.Group>
              </div>
            )}

            {(
              <Form.Group className="mb-3">
                <Form.Label>Client Review and Acceptance Evidence *</Form.Label>
                <Form.Control as="textarea" rows={3} name="acceptanceEvidence" value={acceptanceForm.acceptanceEvidence} onChange={handleAcceptanceFormChange} />
              </Form.Group>
            )}

            <Form.Group controlId="acceptance-entity-type" className="mb-3">
              <Form.Label>Organisation type *</Form.Label>
              <Form.Select name="clientEntityType" value={acceptanceForm.clientEntityType} onChange={handleAcceptanceFormChange}>
                <option value="">Select...</option>
                <option value="company">Company</option><option value="charity">Charity</option><option value="sole_trader">Sole trader / trading as</option><option value="other">Other organisation</option>
              </Form.Select>
            </Form.Group>
            {!isThirdPartyAcceptance && <Form.Group controlId="acceptance-window" className="mb-3"><Form.Label>Approved Go-Live Window *</Form.Label><Form.Control name="approvedGoLiveWindow" value={acceptanceForm.approvedGoLiveWindow} onChange={handleAcceptanceFormChange} /></Form.Group>}
            {([
              ['releaseScope', 'Release scope and acceptance criteria'], ['workReferences', 'Jira, invoice and change references'], ['exclusions', 'Exclusions and deferred items (enter None after review)'],
            ] as const).map(([name, label]) => <Form.Group key={name} controlId={`acceptance-${name}`} className="mb-3"><Form.Label>{label} *</Form.Label><Form.Control as="textarea" rows={3} name={name} value={acceptanceForm[name]} onChange={handleAcceptanceFormChange} /></Form.Group>)}
            <div className="row">
              <Form.Group controlId="acceptance-reviewer" className="mb-3 col-md-6"><Form.Label>Issues reviewed by *</Form.Label><Form.Control name="issuesReviewedBy" value={acceptanceForm.issuesReviewedBy} onChange={handleAcceptanceFormChange} /></Form.Group>
              <Form.Group controlId="acceptance-review-date" className="mb-3 col-md-6"><Form.Label>Issues review date *</Form.Label><Form.Control type="date" name="issuesReviewDate" value={acceptanceForm.issuesReviewDate} onChange={handleAcceptanceFormChange} /></Form.Group>
            </div>
            <Form.Group controlId="acceptance-review-confirmed" className="mb-3"><Form.Label>Confirm known issues and exclusions reviewed *</Form.Label><Form.Select name="knownIssuesReviewStatus" value={acceptanceForm.knownIssuesReviewStatus} onChange={handleAcceptanceFormChange}><option value="">Select...</option><option value="confirmed">Reviewed and confirmed</option></Form.Select></Form.Group>
            <Form.Group controlId="acceptance-signer-title" className="mb-3"><Form.Label>Client signer role / capacity *</Form.Label><Form.Control name="signerTitle" value={acceptanceForm.signerTitle} onChange={handleAcceptanceFormChange} /></Form.Group>

            <div className="row">
              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Known Issues *</Form.Label>
                <Form.Control as="textarea" rows={3} name="knownIssues" value={acceptanceForm.knownIssues} onChange={handleAcceptanceFormChange} />
              </Form.Group>
              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Go-Live Authorisation *</Form.Label>
                <Form.Control as="textarea" rows={3} name="goLiveAuthorisation" value={acceptanceForm.goLiveAuthorisation} onChange={handleAcceptanceFormChange} />
              </Form.Group>
            </div>

            {isThirdPartyAcceptance && (
              <div className="row">
                <Form.Group className="mb-3 col-md-6">
                  <Form.Label>Included Support Allowance *</Form.Label>
                  <Form.Control name="supportAllowance" value={acceptanceForm.supportAllowance} onChange={handleAcceptanceFormChange} />
                </Form.Group>
                <Form.Group className="mb-3 col-md-6">
                  <Form.Label>Additional Support Rate *</Form.Label>
                  <Form.Control name="additionalSupportRate" value={acceptanceForm.additionalSupportRate} onChange={handleAcceptanceFormChange} />
                </Form.Group>
              </div>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Internal Notes</Form.Label>
              <Form.Control as="textarea" rows={2} name="notes" value={acceptanceForm.notes} onChange={handleAcceptanceFormChange} />
            </Form.Group>

            <div className="row">
              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Client Signer Name</Form.Label>
                <Form.Control name="signerName" value={acceptanceForm.signerName} onChange={handleAcceptanceFormChange} />
              </Form.Group>
              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Client Signer Email</Form.Label>
                <Form.Control type="email" name="signerEmail" value={acceptanceForm.signerEmail} onChange={handleAcceptanceFormChange} />
              </Form.Group>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseAcceptanceModal}>Close</Button>
          <Button
            variant="primary"
            onClick={handleGenerateAcceptance}
            disabled={generatingAcceptance || !canGenerateAcceptance}
          >
            {generatingAcceptance ? 'Generating...' : (generatedAcceptance ? 'Generate New Preview' : 'Generate Preview')}
          </Button>
          <Button
            variant="success"
            onClick={handleSendAcceptance}
            disabled={sendingAcceptance || !canSendAcceptance}
          >
            {sendingAcceptance ? 'Sending...' : 'Send with DocuSign'}
          </Button>
        </Modal.Footer>
      </Modal>

      <CreateBitbucketModal
        show={showCreateBitbucketModal}
        onHide={handleCloseCreateBitbucketModal}
        newBitbucketRepo={newBitbucketRepo}
        setNewBitbucketRepo={setNewBitbucketRepo}
        handleCreateBitbucketRepo={handleCreateBitbucketRepo}
        creatingRepoLoading={creatingRepoLoading}      // <-- pass loading to create modal
      />
    </div>
  );
};

export default WebsiteManager;
